import React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { useDispatch, useSelector } from 'react-redux';
import { LinearProgress, Box, Stack, IconButton, Dialog, DialogTitle, DialogContent } from '@mui/material';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import Dropzone from 'react-dropzone';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PermMediaIcon from '@mui/icons-material/PermMedia';
import moment from 'moment/moment';
import { ErrorIcon } from '../../icons/adminIcons';
import CircularProgress from '@mui/material/CircularProgress';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import { TruncatedText } from '../components/truncatedText';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Pages } from '../assets/icons';
import * as pdfjsLib from 'pdfjs-dist';
import useOpenState from '../../../hooks/useOpenState';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import UploadForm, { UploadForm_PrepreUploadMetadata } from './uploadForm';
import APIDocuments from '../apiDocuments';
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const useStyles = makeStyles((theme) => ({
    uploadModalContent: {
        '--pages-icon-color': theme.palette.documentViewer.buttonsBackgroundColor,
        paddingBottom: '16px',
        '& ::-webkit-scrollbar-thumb:hover': {
            background: `${theme.palette.documentViewer.lowContrastTextHoverColor} !important`,
        },
        '& ::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
        },
        '& ::-webkit-scrollbar': {
            width: '5px',
            height: '5px',
        },
        '& ::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.documentViewer.lowContrastTextColor,
        },
    },
    dropzone: {
        marginTop: '16px',
        border: 0,
        backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='12' ry='12' stroke='${theme.palette.documentViewer.tabSelectedBackgroundColor.replace('#', '%23')}' stroke-width='3' stroke-dasharray='10%2c 14' stroke-dashoffset='23' stroke-linecap='square'/%3e%3c/svg%3e")`,
        borderRadius: '8px',
        width: '100%',
        height: '190px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        alignContent: 'center',
        color: theme.palette.documentViewer.tabSelectedBackgroundColor,
    },
    uploadModalContentNotSupportedInfo: {
        border: `2px solid ${theme.palette.documentViewer.uploadModalWarningColor}`,
        backgroundColor: theme.palette.documentViewer.uploadModalWarningBackground,
        padding: '12px',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: 'bold',
        gap: '8px',
        color: theme.palette.documentViewer.uploadModalWarningColor,
    },
    uploadZone: {
        height: '100%',
        width: '100%',
        textAlign: 'center',
    },
    uploadZoneContent: {
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    loadingBox: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '20px',
        padding: '10px',
    },
    fileUploadStack: {
        maxHeight: '80vh',
        overflowY: 'auto',
        '& .file-upload-item': {
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            padding: '8px',
            gap: '16px',
            '& .file-upload-item-thumbnail': {
                width: '210px',
                maxWidth: '210px',
                minWidth: '210px',
                wordWrap: 'break-word',
                paddingLeft: '5px',
                paddingRight: '5px',
                '& .thumbnail': {
                    position: 'relative',
                    '& .thumb-actions': {
                        position: 'absolute',
                        top: '5px',
                        right: '5px',
                        gap: '4px',
                        '& .button-thumbnail': {
                            background: theme.palette.documentViewer.buttonsBackgroundColor,
                            borderRadius: '6px',
                            width: '24px',
                            height: '24px',
                            '&.delete-button': {
                                background: theme.palette.documentViewer.traficLightRed,
                            },
                            '& svg': {
                                fontSize: '16px',
                            },
                        },
                    },
                    '& .document-preview-box': {
                        width: '192px',
                        borderRadius: '8px',
                        backgroundRepeat: 'no-repeat',
                        backgroundAttachment: 'fixed',
                        backgroundPosition: 'center',
                        backgroundSize: '100%',
                        border: '1px solid var(--pages-icon-color)',
                        '&.landscape': {
                            height: '152px',
                        },
                        '&.portrait': {
                            height: '252px',
                        },
                    },
                    '& .page-count-mark': {
                        position: 'absolute',
                        bottom: '36px',
                        left: '8px',
                        width: '30px',
                        '& .mark-icon': {
                            marginLeft: 'auto',
                            marginRight: 'auto',
                            width: '30px',
                            height: '30px',
                            position: 'absolute',
                        },
                        '& .mark-text': {
                            position: 'absolute',
                            marginLeft: 'auto',
                            marginRight: 'auto',
                            textAlign: 'center',
                            lineHeight: '30px',
                            width: '22px',
                            fontSize: '12px',
                            color: 'white',
                        },
                    },
                },
            },
        },
    },
    dialogReorderTitle: {
        width: '500px',
    },
    dialogReorderContent: {
        width: '500px',
    },
}));

export function DocUploadForm(props) {
    const { t } = useTranslation();
    const classes = useStyles();
    const dispatch = useDispatch();
    const config = window.envConfig.documentViewer.uploadFields;
    const USER = useSelector((state) => state.user.userData);
    const fileInputRef = React.useRef(null);
    const [filesToUpload, setFilesToUpload] = React.useState([]);
    const [fileTypeUnsupportedInfoShow, setFileTypeUnsupportedInfoShow] = React.useState(false);
    const [bigViewMode, setBigViewMode] = React.useState(-1);
    const [uploadTry, setUploadTry] = React.useState(0);
    const [thumbPreparing, setThumbPreparing] = React.useState(false);
    const [isReorderOpen, reorderOpen, reorderClose] = useOpenState(false, true);
    const [validatingStatus, setValidatingStatus] = React.useState({});
    const [uploadFormsValid, setUploadFormsValid] = React.useState(false);
    const [processingStatus, setProcessingStatus] = React.useState({});
    const [uploadProcessing, setUploadProcessing] = React.useState(false);
    const [progressStep, setProgressStep] = React.useState(null);

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 B';
        const units = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
    };

    const getFileIcon = (type) => {
        switch (type) {
            case 'application/pdf':
                return <PictureAsPdfIcon />;
            case 'image/jpeg':
            case 'image/png':
            case 'image/gif':
            case 'image/bmp':
            case 'image/avif':
                return <PermMediaIcon />;
            case 'video/mp4':
                return <VideoFileIcon />;
            default:
                return <InsertDriveFileIcon />;
        }
    };

    const changeFile = (fileData, filesRejected, event) => {
        setThumbPreparing(true);
        setFileTypeUnsupportedInfoShow(false);
        if (fileData && fileData.length > 0) {
            const readURL = (file) => {
                return new Promise((res, rej) => {
                    const reader = new FileReader();
                    reader.onload = async (e) => {
                        const image = e.target.result;
                        let dimensions = await getImageDimensions(image);
                        res([image, 1, dimensions.width, dimensions.height]);
                    };
                    reader.onerror = (e) => rej(e);
                    reader.readAsDataURL(file);
                });
            };

            const getImagePagePDF = async (pageNo, fileObj) => {
                // const arrayBuffer = await readURL(fileObj);
                const arrayBuffer = await fileObj.arrayBuffer();
                const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
                const page = await pdf.getPage(pageNo); // 1-indexed
                const pageCount = pdf.numPages;
                const viewport = page.getViewport({ scale: 1 }); // scale for quality
                const canvas = document.createElement('canvas');
                canvas.width = viewport.width;
                canvas.height = viewport.height;
                await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
                const firstPageBytes = canvas.toDataURL('image/png');
                return [firstPageBytes, pageCount, viewport.width, viewport.height];
            };
            function getImageDimensions(base64) {
                return new Promise((resolve) => {
                    const img = new Image();
                    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
                    img.src = base64;
                });
            }
            let newFilesToUpload = [];
            let fDataPromises = [];
            fileData.forEach((file) => {
                newFilesToUpload.push({
                    id: _.uniqueId('file_to_upload'),
                    name: file.name,
                    size: file.size,
                    pageCount: 1,
                    previewData: null,
                    formData: null,
                    type: file.type,
                    docDate: moment(file.lastModifiedDate),
                    fileObj: file,
                    thumbnailImageData: null,
                    icon: getFileIcon(file.type),
                });

                if (file.type === 'application/pdf') {
                    fDataPromises.push(getImagePagePDF(1, file));
                } else {
                    fDataPromises.push(readURL(file));
                }
            });
            Promise.all(fDataPromises).then((data) => {
                for (let i = 0; i < data.length; i++) {
                    if (newFilesToUpload[i].type === 'application/pdf') {
                        newFilesToUpload[i].pageCount = data[i][1];
                    }
                    newFilesToUpload[i].previewData = data[i][0];
                    newFilesToUpload[i].width = data[i][2];
                    newFilesToUpload[i].height = data[i][3];
                }
                setFilesToUpload((prevFileToUpload) => {
                    if (_.isNull(prevFileToUpload)) return [...newFilesToUpload];
                    return [...prevFileToUpload, ...newFilesToUpload];
                });
                setThumbPreparing(false);
            });
        } else {
            if (filesRejected && filesRejected.length > 0) {
                setFileTypeUnsupportedInfoShow(true);
            }
            setThumbPreparing(false);
        }
    };

    React.useEffect(() => {
        if (props && props.uploadFilesCount) props.uploadFilesCount(filesToUpload.length);
    }, [filesToUpload]);

    const uploadFormData = (idx, formData) => {
        let newFilesToUpload = _.cloneDeep(filesToUpload);
        if (newFilesToUpload.length > idx) {
            newFilesToUpload[idx].formData = formData;
            setFilesToUpload(newFilesToUpload);
        }
    };

    const removeUploadItem = (fileId) => {
        setFilesToUpload((fileDataPrev) => {
            let fileDataTmp = _.cloneDeep(fileDataPrev);
            _.remove(fileDataTmp, (file) => file.id === fileId);
            return [...fileDataTmp];
        });
    };

    const uploadProceed = (event) => {
        if (filesToUpload.length > 0) {
            let validationResult = {};

            setUploadFormsValid(false);
            filesToUpload.forEach((file) => {
                validationResult[file.id] = null;
            });
            setValidatingStatus(validationResult);
            setUploadTry((prev) => {
                return 1 + prev;
            });
        }
    };

    const uploadProceedValidationResult = (result) => {
        setValidatingStatus((prev) => {
            let before = _.cloneDeep(prev);
            before[result.form] = result.valid;
            return before;
        });
    };

    React.useEffect(() => {
        if (!uploadFormsValid) {
            let totalStatus = true;
            filesToUpload.forEach((file) => {
                const status = _.get(validatingStatus, file.id, null);
                totalStatus = totalStatus && status !== null && status;
            });
            setUploadFormsValid(totalStatus);
        }
    }, [validatingStatus]);

    React.useEffect(() => {
        if (uploadFormsValid && filesToUpload.length > 0) {
            let processingResult = {};
            filesToUpload.forEach((file) => {
                processingResult[file.id] = 0;
            });
            setProcessingStatus(processingResult);
            setUploadProcessing(true);
        }
    }, [uploadFormsValid]);

    React.useEffect(() => {
        if (uploadProcessing) {
            filesToUpload.forEach((file) => {
                APIDocuments.uploadDocumentDataXHR(
                    file.fileObj,
                    UploadForm_PrepreUploadMetadata(file.formData, props.catalog),
                    (progress) => {
                        setProgressStep({ fileId: file.id, progress: progress });
                    },
                )
                    .then(([success, response]) => {
                        console.log('final', success, response);
                    })
                    .catch((error) => {
                        console.log('getting error', error);
                    });
            });
        }
    }, [uploadProcessing]);

    React.useEffect(() => {
        if (progressStep) {
            if (progressStep.fileId != null) {
                setProcessingStatus((prev) => {
                    let before = _.cloneDeep(prev);
                    before[progressStep.fileId] = progressStep.progress;
                    return before;
                });
            }
        }
    }, [progressStep]);

    return (
        <>
            <Box className={classes.uploadModalContent}>
                {config.uploadUnsupportedTypes && (
                    <Stack direction={'row'} justifyContent={'flex-start'} className={classes.uploadModalContentNotSupportedInfo}>
                        <ErrorIcon />
                        <Box>{`${t('System does not support')} ${config.uploadUnsupportedTypes.map((type) => type.display).join(', ')} ${t('Files')}.`}</Box>
                    </Stack>
                )}
                <Dropzone
                    onDrop={changeFile}
                    multiple={true}
                    accept={config.hasOwnProperty('uploadAcceptFileTypes') ? config.uploadAcceptFileTypes : null}
                >
                    {({ getRootProps, getInputProps }) => (
                        <>
                            <input {...getInputProps()} ref={fileInputRef} />
                            {thumbPreparing && (
                                <Box className={classes.loadingBox}>
                                    {t('Reading files...')}
                                    <CircularProgress color="inherit" size={64} />
                                </Box>
                            )}
                            {filesToUpload.length === 0 && !thumbPreparing && !uploadProcessing && (
                                <section className={classes.dropzone}>
                                    <div {...getRootProps()} className={classes.uploadZone}>
                                        <div className={classes.uploadZoneContent}>
                                            {t('Drag File to Upload')}
                                            {config.hasOwnProperty('uploadAcceptFileTypes') && (
                                                <span>&nbsp;&nbsp;(&nbsp;{config.uploadAcceptFileTypes} )</span>
                                            )}
                                            {fileTypeUnsupportedInfoShow && (
                                                <span style={{ color: 'red', fontWeight: 600 }}>
                                                    &nbsp;&nbsp;{t('Unsupported file type')}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </section>
                            )}
                            {filesToUpload.length > 0 && !thumbPreparing && !uploadProcessing && (
                                <>
                                    {!thumbPreparing && (
                                        <>
                                            <Box padding={'8px'}>{`${t('Documents')} (${filesToUpload.length})`}</Box>
                                            <Stack direction={'column'} className={classes.fileUploadStack} gap={'25px'}>
                                                {filesToUpload.map((file, index) => (
                                                    <div key={`file-upload-${index}`} className="file-upload-item">
                                                        <div className="file-upload-item-thumbnail">
                                                            <Stack direction={'column'} spacing={'10px'} alignItems={'center'}>
                                                                <Box className={`thumbnail`}>
                                                                    <Stack
                                                                        direction={'row'}
                                                                        spacing={'3px'}
                                                                        alignItems={'center'}
                                                                        className={'thumb-actions'}
                                                                    >
                                                                        {filesToUpload.length > 1 && (
                                                                            <IconButton
                                                                                className={'button-thumbnail'}
                                                                                onClick={() => reorderOpen()}
                                                                            >
                                                                                <DragIndicatorIcon />
                                                                            </IconButton>
                                                                        )}
                                                                        <IconButton className={'button-thumbnail'}>
                                                                            <FullscreenIcon />
                                                                        </IconButton>
                                                                        <IconButton
                                                                            className={'button-thumbnail delete-button'}
                                                                            onClick={() => {
                                                                                removeUploadItem(file.id);
                                                                            }}
                                                                        >
                                                                            <DeleteForeverIcon />
                                                                        </IconButton>
                                                                    </Stack>
                                                                    <Box
                                                                        id={`pdf-preview-${file.id}`}
                                                                        className={`document-preview-box ${file.width > file.height ? 'landscape' : 'portrait'}`}
                                                                        sx={{ backgroundImage: `url(${file.previewData})` }}
                                                                    ></Box>
                                                                    <div className={'page-count-mark'}>
                                                                        <Pages className={'mark-icon'} color={'var(--pages-icon-color)'} />
                                                                        <div className={'mark-text'}>{file.pageCount}</div>
                                                                    </div>
                                                                </Box>
                                                                <Stack
                                                                    direction={'row'}
                                                                    spacing={'3px'}
                                                                    alignItems={'center'}
                                                                    flex={'1 1 auto'}
                                                                >
                                                                    <Box>{file.icon}</Box>
                                                                    <Box maxWidth={'186px'}>
                                                                        <TruncatedText text={file.name} />
                                                                    </Box>
                                                                </Stack>
                                                            </Stack>
                                                        </div>
                                                        <UploadForm
                                                            index={index}
                                                            fileData={file.formData}
                                                            onChange={(formData) => uploadFormData(index, formData)}
                                                            formId={file.id}
                                                            uploadValidate={uploadTry}
                                                            onValidate={uploadProceedValidationResult}
                                                            attendances={props.attendances}
                                                            referrals={props.referrals}
                                                        ></UploadForm>
                                                    </div>
                                                ))}
                                            </Stack>
                                        </>
                                    )}
                                </>
                            )}
                            {filesToUpload.length > 0 && !thumbPreparing && uploadProcessing && (
                                <Stack>
                                    {filesToUpload.map((file, index) => (
                                        <Stack key={`file-upload-progress-${index}`}>
                                            <Stack>
                                                <Stack>
                                                    <Box>{file.name}</Box>
                                                    <Box>{formatFileSize(file.size)}</Box>
                                                </Stack>
                                                <Box>status</Box>
                                            </Stack>
                                            <Box sx={{ width: '100%' }}>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={processingStatus[file.id]}
                                                    aria-label={t('Upload data')}
                                                />
                                            </Box>
                                        </Stack>
                                    ))}
                                </Stack>
                            )}
                        </>
                    )}
                </Dropzone>
            </Box>
            <Stack direction={'row'} justifyContent={'flex-end'} alignItems={'center'} gap={'10px'}>
                <Button variant={'outlined'} onClick={() => fileInputRef.current.click()} disabled={uploadProcessing}>
                    {t('Choose file')}
                </Button>
                <Button variant={'contained'} onClick={uploadProceed} disabled={filesToUpload.length === 0 || uploadProcessing}>
                    {t('Upload')}
                </Button>
            </Stack>
            <Dialog open={isReorderOpen}>
                <DialogTitle className={classes.dialogReorderTitle}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>Reorder Files</Box>
                        <IconButton onClick={reorderClose}>
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                </DialogTitle>
                <DialogContent className={classes.dialogReorderContent}>
                    <Stack direction="row">
                        <InfoIcon />
                        <Box>{t('Drag and Drop to Reorder Files.')}</Box>
                    </Stack>
                </DialogContent>
            </Dialog>
        </>
    );
}
